import { useEffect, useState } from 'react';
import { api, errMsg } from '../../lib/api';

interface NotaryConfig {
  enabled: boolean;
  chain: string;
  rpcUrl: string;
  contract: string;
  hasPrivateKey: boolean;
  usingEnvKey: boolean;
  ready: boolean;
}

interface NotaryRecord {
  id: string;
  memorialId: string;
  contentHash: string;
  txHash: string | null;
  blockNumber: number | null;
  chain: string;
  status: string;
  error: string | null;
  notarizedAt: number | null;
  createdAt: number;
}

const STATUS_LABEL: Record<string, string> = {
  pending: '排队中',
  confirmed: '已上链',
  failed: '失败',
};

const STATUS_CLS: Record<string, string> = {
  pending: 'bg-amber-50 text-amber-700',
  confirmed: 'bg-emerald-50 text-emerald-700',
  failed: 'bg-red-50 text-red-600',
};

export default function AdminNotary() {
  const [cfg, setCfg] = useState<NotaryConfig | null>(null);
  const [records, setRecords] = useState<NotaryRecord[]>([]);
  const [form, setForm] = useState({
    enabled: false,
    chain: 'base',
    rpcUrl: '',
    contract: '',
    privateKey: '',
  });
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');

  async function load() {
    const [c, r] = await Promise.all([
      api<NotaryConfig>('/notary/config'),
      api<{ items: NotaryRecord[] }>('/notary/records'),
    ]);
    if (c.ok && c.data) {
      setCfg(c.data);
      setForm({
        enabled: c.data.enabled,
        chain: c.data.chain,
        rpcUrl: c.data.rpcUrl,
        contract: c.data.contract,
        privateKey: '',
      });
    }
    if (r.ok && r.data) setRecords(r.data.items);
  }

  useEffect(() => {
    load();
  }, []);

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError('');
    setNotice('');

    const res = await api('/notary/config', {
      method: 'PUT',
      body: JSON.stringify(form),
    });

    setSaving(false);
    if (res.ok) {
      setNotice('配置已保存');
      load();
    } else {
      setError(errMsg(res, '保存失败'));
    }
  }

  if (!cfg) return <p className="text-sm text-ink-500">加载中…</p>;

  return (
    <div>
      {/* 状态横幅 */}
      <div
        className={`mb-6 rounded-xl border p-4 text-xs leading-relaxed ${
          cfg.ready
            ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
            : 'border-amber-200 bg-amber-50 text-amber-800'
        }`}
      >
        {cfg.ready ? (
          <>存证功能已启用，配置完整。购买存证套餐的订单会自动进入上链队列。</>
        ) : (
          <>
            存证功能尚未启用。下面的配置填完并打开开关后即可生效——
            <strong>没填之前，购买入口会自动隐藏，其他功能完全不受影响。</strong>
          </>
        )}
      </div>

      <form onSubmit={save} className="max-w-xl rounded-2xl border border-ink-200 bg-white p-6">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-sm font-medium text-ink-900">链上存证配置</h2>
          <label className="flex items-center gap-2 text-xs text-ink-600">
            <input
              type="checkbox"
              checked={form.enabled}
              onChange={(e) => setForm((f) => ({ ...f, enabled: e.target.checked }))}
            />
            启用
          </label>
        </div>

        <div className="mb-4">
          <label className="mb-1.5 block text-xs text-ink-600">链网络</label>
          <select
            value={form.chain}
            onChange={(e) => setForm((f) => ({ ...f, chain: e.target.value }))}
            className="h-11 w-full rounded-xl border border-ink-200 bg-ink-50/50 px-3 text-sm outline-none focus:border-ink-500 focus:bg-white"
          >
            <option value="base">Base 主网</option>
            <option value="base-sepolia">Base Sepolia 测试网</option>
          </select>
        </div>

        <div className="mb-4">
          <label className="mb-1.5 block text-xs text-ink-600">RPC 地址</label>
          <input
            value={form.rpcUrl}
            onChange={(e) => setForm((f) => ({ ...f, rpcUrl: e.target.value }))}
            placeholder="https://mainnet.base.org"
            className="h-11 w-full rounded-xl border border-ink-200 bg-ink-50/50 px-3 font-mono text-xs outline-none focus:border-ink-500 focus:bg-white"
          />
          <p className="mt-1 text-[11px] text-ink-400">公共端点免费可用，也可填 Alchemy 等服务的地址</p>
        </div>

        <div className="mb-4">
          <label className="mb-1.5 block text-xs text-ink-600">存证合约地址</label>
          <input
            value={form.contract}
            onChange={(e) => setForm((f) => ({ ...f, contract: e.target.value }))}
            placeholder="0x..."
            className="h-11 w-full rounded-xl border border-ink-200 bg-ink-50/50 px-3 font-mono text-xs outline-none focus:border-ink-500 focus:bg-white"
          />
        </div>

        <div className="mb-5">
          <label className="mb-1.5 block text-xs text-ink-600">平台钱包私钥</label>
          {cfg.usingEnvKey ? (
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2.5 text-xs text-emerald-700">
              已通过环境变量（wrangler secret）配置，此处无需填写，也不会写入数据库。
            </div>
          ) : (
            <>
              <input
                type="password"
                value={form.privateKey}
                onChange={(e) => setForm((f) => ({ ...f, privateKey: e.target.value }))}
                placeholder={cfg.hasPrivateKey ? '已设置，留空表示不修改' : '输入私钥'}
                className="h-11 w-full rounded-xl border border-ink-200 bg-ink-50/50 px-3 font-mono text-xs outline-none focus:border-ink-500 focus:bg-white"
              />
              <p className="mt-1.5 text-[11px] leading-relaxed text-red-600">
                安全提醒：私钥不会回显，但会存入数据库。强烈建议改用
                <code className="mx-1 rounded bg-ink-100 px-1">wrangler secret put NOTARY_PRIVATE_KEY</code>
                更安全。钱包内只放小额 ETH。
              </p>
            </>
          )}
        </div>

        {error && (
          <p className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-xs text-red-600">{error}</p>
        )}
        {notice && (
          <p className="mb-3 rounded-lg bg-emerald-50 px-3 py-2 text-xs text-emerald-700">
            {notice}
          </p>
        )}

        <button
          type="submit"
          disabled={saving}
          className="h-11 rounded-xl bg-ink-900 px-8 text-sm text-ink-50 transition hover:bg-ink-800 disabled:opacity-50"
        >
          {saving ? '保存中…' : '保存配置'}
        </button>
      </form>

      {/* 记录 */}
      <h2 className="mb-4 mt-10 text-sm font-medium text-ink-900">
        存证记录（{records.length}）
      </h2>

      {records.length === 0 ? (
        <p className="text-xs text-ink-500">暂无记录。配置启用后可通过接口提交存证。</p>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-ink-200 bg-white">
          <table className="w-full text-sm">
            <thead className="border-b border-ink-100 bg-ink-50/60 text-left text-xs text-ink-600">
              <tr>
                <th className="px-4 py-3">纪念馆</th>
                <th className="px-4 py-3">内容指纹</th>
                <th className="px-4 py-3">交易哈希</th>
                <th className="px-4 py-3">区块</th>
                <th className="px-4 py-3">状态</th>
              </tr>
            </thead>
            <tbody>
              {records.map((r) => (
                <tr key={r.id} className="border-b border-ink-50 last:border-0">
                  <td className="px-4 py-3 font-mono text-[10px] text-ink-600">
                    {r.memorialId.slice(0, 8)}…
                  </td>
                  <td className="px-4 py-3 font-mono text-[10px] text-ink-600">
                    {r.contentHash.slice(0, 14)}…
                  </td>
                  <td className="px-4 py-3 font-mono text-[10px] text-ink-600">
                    {r.txHash ? `${r.txHash.slice(0, 14)}…` : '—'}
                  </td>
                  <td className="px-4 py-3 text-xs text-ink-500">
                    {r.blockNumber ? `#${r.blockNumber}` : '—'}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`rounded-full px-2 py-0.5 text-[10px] ${STATUS_CLS[r.status] ?? ''}`}
                    >
                      {STATUS_LABEL[r.status] ?? r.status}
                    </span>
                    {r.error && (
                      <p className="mt-1 text-[10px] text-red-600">{r.error}</p>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
